/**
 * Delivery plans (Stock idea 8): saved, shared plans of what goes to which project on which day. The
 * Stock tab shows a slim callout; plans open in a side panel; on the day "Convert to Site DN" opens
 * Record movement already filled in, and the plan is closed with that DN's reference.
 */
import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronRight, ClipboardPaste, Pencil, Plus, Trash2, Truck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate, formatNumber, formatRelativeDays, toDateInputValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Callout, PersonAvatar } from './InventoryUi';
import { INVENTORY_LOCATIONS, formatQty, locationShort, parsePastedRows, plural, toQty } from './inventoryModel';

const fieldLabel = 'mb-1 block text-[12px] font-semibold text-ink-3';
const ANY_STORE = '__any__';

const blankLine = () => ({ key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, part_number: '', qty: '' });

export function DeliveryPlansCallout({ plans = [], loading, error, onOpenList, onNew, className }) {
  const drafts = plans.filter((plan) => plan.status === 'Draft');
  const next = drafts[0];
  return (
    <Callout
      tone="navy"
      icon={Truck}
      className={className}
      data-testid="stock-delivery-plans"
      title={(
        <>
          <b className="font-bold">Delivery plans</b>
          <span className="text-ink-2"> · {error ? 'couldn’t load' : loading && !plans.length ? 'loading…' : plural(drafts.length, 'draft')}</span>
        </>
      )}
      subWrap
      sub={next
        ? <>Next: <b className="font-semibold text-ink">{formatDate(next.planned_date, { year: 'auto', weekday: true })}</b> · {next.project_name} · {plural(next.item_count, 'item')}</>
        : 'Plan items per project and day, then turn a plan into a Site DN.'}
      action={(
        <span className="flex items-center gap-2">
          {plans.length ? (
            <Button type="button" size="sm" variant="ghost" className="text-ink-2" onClick={onOpenList} data-testid="open-delivery-plans">
              View plans
            </Button>
          ) : null}
          {onNew ? (
            <Button type="button" size="sm" variant="outline" className="text-ink" onClick={onNew} data-testid="new-delivery-plan">
              <Plus aria-hidden="true" /> New delivery plan
            </Button>
          ) : null}
        </span>
      )}
    />
  );
}

/**
 * Phones (18 #8): one tappable line instead of the callout — "Delivery plans · 2 drafts ›" — that opens the
 * plans panel (Convert to Site DN is there). Shown only while there are drafts.
 */
export function DeliveryPlansPhoneRow({ plans = [], onOpenList, className }) {
  const drafts = plans.filter((plan) => plan.status === 'Draft');
  if (!drafts.length) return null;
  return (
    <button
      type="button"
      onClick={onOpenList}
      className={cn('flex h-11 w-full items-center gap-2.5 rounded-xl border border-[color:var(--navy-100)] bg-[color:var(--navy-50)] px-3.5 text-left text-[13px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
      data-testid="stock-delivery-plans-phone"
    >
      <Truck className="h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate"><b className="font-bold">Delivery plans</b><span className="text-ink-2"> · {plural(drafts.length, 'draft')}</span></span>
      <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
    </button>
  );
}

/**
 * Convert / Edit / Cancel follow the server's rule (_can_edit_plan via `canChange`): the plan's author,
 * Admin, Super Admin or Storekeeper. A plan whose Site DN could not issue every line stays a draft with
 * the lines left, and says which DN took the rest.
 */
export function DeliveryPlansPanel({ open, onOpenChange, plans = [], onNew, onEdit, onConvert, onCancelPlan, canChange, canCreate = true }) {
  const drafts = plans.filter((plan) => plan.status === 'Draft');
  const converted = plans
    .filter((plan) => plan.status === 'Converted')
    .sort((left, right) => String(right.converted_at || right.updated_at || '').localeCompare(String(left.converted_at || left.updated_at || '')))
    .slice(0, 5);
  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      icon={Truck}
      title="Delivery plans"
      subtitle={`${plural(drafts.length, 'draft')} · soonest first · converting opens a pre-filled Site DN`}
      actions={canCreate ? <Button type="button" size="sm" variant="outline" className="text-ink" onClick={onNew}><Plus aria-hidden="true" /> New plan</Button> : null}
      hideFooter
      testId="delivery-plans-panel"
    >
      {drafts.length ? (
        <ul className="space-y-3">
          {drafts.map((plan) => {
            const late = (toDateInputValue(plan.planned_date) < toDateInputValue(new Date()));
            return (
              <li key={plan.id} className="rounded-xl border border-line bg-surface p-4 shadow-card" data-testid={`delivery-plan-${plan.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-[14px] font-bold text-ink">
                      <CalendarDays className="h-4 w-4 text-ink-3" aria-hidden="true" />
                      {formatDate(plan.planned_date, { weekday: true })}
                      <span className={cn('text-[12px] font-semibold', late ? 'text-status-red-ink' : 'text-ink-3')}>
                        {formatRelativeDays(plan.planned_date, { style: 'due' })}
                      </span>
                    </p>
                    <p className="mt-1 text-[13px] text-ink">
                      <b className="font-semibold">{plan.project_name || 'Unknown project'}</b>
                      <span className="text-ink-3"> · {plural(plan.item_count, 'item')} · {formatQty(plan.total_qty)} pcs{plan.source_location ? ` · from ${locationShort(plan.source_location)}` : ''}</span>
                    </p>
                  </div>
                  {canChange?.(plan) ? (
                    <Button type="button" size="sm" onClick={() => onConvert?.(plan)} data-testid={`convert-plan-${plan.id}`}>
                      <Truck aria-hidden="true" /> Convert to Site DN
                    </Button>
                  ) : null}
                </div>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {(plan.items || []).slice(0, 6).map((line) => (
                    <span key={line.part_number} className="inline-flex h-[22px] items-center gap-1.5 rounded-md border border-line bg-surface-2 px-2 text-[11.5px] text-ink-2">
                      <span className="font-mono">{line.part_number}</span><b className="tabular-nums text-ink">×{formatQty(line.qty)}</b>
                    </span>
                  ))}
                  {(plan.items || []).length > 6 ? <span className="self-center text-[11.5px] text-ink-3">+{plan.items.length - 6} more</span> : null}
                </div>
                {plan.notes ? <p className="mt-2 text-[12.5px] text-ink-2">{plan.notes}</p> : null}
                {(plan.partial_conversions || []).length ? (
                  <p className="mt-2 text-[12px] text-status-amber-ink" data-testid={`delivery-plan-partial-${plan.id}`}>
                    Partly issued on {plan.partial_conversions.map((entry) => entry.dn_reference).filter(Boolean).join(', ')} · these lines are still to send
                  </p>
                ) : null}
                <div className="mt-3 flex items-center gap-2 border-t border-line-2 pt-2.5 text-[12px] text-ink-3">
                  <PersonAvatar name={plan.created_by} size={18} />
                  <span className="min-w-0 flex-1 truncate">{plan.created_by || '—'} · {formatDate(plan.created_at, { year: 'auto' })}</span>
                  {canChange?.(plan) ? (
                    <>
                      <Button type="button" size="xs" variant="ghost" className="text-ink-2" onClick={() => onEdit?.(plan)}><Pencil aria-hidden="true" /> Edit</Button>
                      <Button type="button" size="xs" variant="ghost" className="text-ink-2" onClick={() => onCancelPlan?.(plan)} data-testid={`cancel-plan-${plan.id}`}><Trash2 aria-hidden="true" /> Cancel plan</Button>
                    </>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState icon={Truck} title="No draft delivery plans" text="Plan what goes to a site and when; the store converts it into a Site DN on the day." action={canCreate ? <Button type="button" size="sm" variant="outline" className="text-ink" onClick={onNew}><Plus aria-hidden="true" /> New delivery plan</Button> : null} />
      )}
      {converted.length ? (
        <SidePanelSection title="Recently converted">
          <ul className="space-y-1.5">
            {converted.map((plan) => (
              <li key={plan.id} className="flex items-center gap-2 text-[12.5px] text-ink-2">
                <StatusPill tone="green" label="Converted" size="sm" />
                <span className="min-w-0 flex-1 truncate">{plan.project_name} · {formatDate(plan.planned_date, { year: 'auto' })}</span>
                <span className="font-mono text-[12px] text-ink">{plan.dn_reference}</span>
              </li>
            ))}
          </ul>
        </SidePanelSection>
      ) : null}
    </SidePanel>
  );
}

/**
 * New / edit plan: project, delivery date, store, items (typed or pasted from Excel: part no. + qty).
 * `stock` gives the part-number suggestions and the "N available in …" hint per line.
 */
export function DeliveryPlanEditor({ open, onOpenChange, plan, projects = [], stock = [], onSave }) {
  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState('');
  const [store, setStore] = useState(ANY_STORE);
  const [lines, setLines] = useState([blankLine()]);
  const [notes, setNotes] = useState('');
  const [paste, setPaste] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [initial, setInitial] = useState('');

  useEffect(() => {
    if (!open) return;
    const startLines = plan?.items?.length
      ? plan.items.map((line) => ({ ...blankLine(), part_number: line.part_number, qty: String(line.qty) }))
      : (plan?.prefill || []).length
        ? plan.prefill.map((line) => ({ ...blankLine(), part_number: line.part_number, qty: line.qty ? String(line.qty) : '' }))
        : [blankLine()];
    setProjectId(plan?.project_id || '');
    setDate(toDateInputValue(plan?.planned_date) || '');
    setStore(plan?.source_location || ANY_STORE);
    setLines(startLines);
    setNotes(plan?.notes || '');
    setPaste('');
    setPasteOpen(false);
    setInitial(JSON.stringify([plan?.project_id || '', toDateInputValue(plan?.planned_date) || '', plan?.source_location || ANY_STORE, startLines.map((l) => [l.part_number, l.qty]), plan?.notes || '']));
  }, [open, plan]);

  const stockByPart = useMemo(() => new Map(stock.map((item) => [String(item.part_number).toUpperCase(), item])), [stock]);
  const partOptions = useMemo(() => stock.filter((item) => item.part_number).map((item) => item.part_number).sort(), [stock]);
  const current = JSON.stringify([projectId, date, store, lines.map((l) => [l.part_number, l.qty]), notes]);
  const dirty = open && current !== initial;
  const filled = lines.filter((line) => String(line.part_number).trim() || String(line.qty).trim());

  const setLine = (key, field, value) => setLines((rows) => rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  const removeLine = (key) => setLines((rows) => (rows.length === 1 ? [blankLine()] : rows.filter((row) => row.key !== key)));
  const importPaste = () => {
    const rows = parsePastedRows(paste, ['part_number', 'qty'], {
      part_number: ['part', 'model', 'item', 'code'],
      qty: ['qty', 'quantity', 'pcs']
    }).filter((row) => row.part_number);
    if (!rows.length) return;
    setLines((current) => {
      const kept = current.filter((row) => String(row.part_number).trim() || String(row.qty).trim());
      return [...kept, ...rows.map((row) => ({ ...blankLine(), part_number: row.part_number, qty: String(toQty(row.qty) || '') }))];
    });
    setPaste('');
    setPasteOpen(false);
  };

  const save = async () => {
    if (!projectId) throw new Error('Pick the project the items go to');
    if (!date) throw new Error('Pick the delivery date');
    const items = filled.map((line, index) => {
      const qty = Number(line.qty);
      if (!String(line.part_number).trim()) throw new Error(`Line ${index + 1}: part number is missing`);
      if (!Number.isFinite(qty) || qty <= 0) throw new Error(`Line ${index + 1} (${line.part_number}): enter a quantity above 0`);
      return { part_number: String(line.part_number).trim(), qty, description: stockByPart.get(String(line.part_number).trim().toUpperCase())?.description || '' };
    });
    if (!items.length) throw new Error('Add at least one item');
    await onSave?.({
      project_id: projectId,
      planned_date: date,
      source_location: store === ANY_STORE ? '' : store,
      items,
      notes
    });
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="lg"
      icon={Truck}
      title={plan?.id ? 'Edit delivery plan' : 'New delivery plan'}
      subtitle="Saved for everyone · changes no stock until it is converted to a Site DN"
      dirty={dirty}
      onSave={save}
      saveLabel={plan?.id ? 'Save plan' : 'Save plan'}
      testId="delivery-plan-editor"
    >
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <label className="block min-w-0">
          <span className={fieldLabel}>Project</span>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="rounded-control border-line bg-surface text-ink" data-testid="delivery-plan-project"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {projects.map((project) => <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="block">
          <span className={fieldLabel}>Delivery to site</span>
          <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} data-testid="delivery-plan-date" />
        </label>
        <label className="block min-w-0">
          <span className={fieldLabel}>From store</span>
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="rounded-control border-line bg-surface text-ink"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_STORE}>Decide on the day</SelectItem>
              {INVENTORY_LOCATIONS.map((location) => <SelectItem key={location} value={location}>{location}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
      </div>

      <SidePanelSection
        title={`Items · ${plural(filled.length, 'line')}`}
        actions={(
          <span className="flex items-center gap-1">
            <Button type="button" size="xs" variant="ghost" className="text-ink-2" onClick={() => setPasteOpen((value) => !value)} aria-expanded={pasteOpen}>
              <ClipboardPaste aria-hidden="true" /> Paste from Excel
            </Button>
            <Button type="button" size="xs" variant="outline" className="text-ink" onClick={() => setLines((rows) => [...rows, blankLine()])} data-testid="delivery-plan-add-line">
              <Plus aria-hidden="true" /> Add item
            </Button>
          </span>
        )}
      >
        {pasteOpen ? (
          <div className="mb-3 rounded-xl border border-line bg-surface-2 p-3">
            <Textarea
              value={paste}
              onChange={(event) => setPaste(event.target.value)}
              placeholder={'Part no.\tQty\nMTN693003\t3\n4701060\t12'}
              className="min-h-[90px] font-mono text-[12px]"
              aria-label="Paste part numbers and quantities"
              data-testid="delivery-plan-paste"
            />
            <div className="mt-2 flex justify-end gap-2">
              <Button type="button" size="sm" variant="ghost" onClick={() => setPasteOpen(false)}>Close</Button>
              <Button type="button" size="sm" variant="outline" className="text-ink" onClick={importPaste} disabled={!paste.trim()}>Add pasted lines</Button>
            </div>
          </div>
        ) : null}
        <datalist id="delivery-plan-parts">
          {partOptions.map((partNumber) => <option key={partNumber} value={partNumber} />)}
        </datalist>
        <div className="space-y-2">
          {lines.map((line, index) => {
            const stockItem = stockByPart.get(String(line.part_number).trim().toUpperCase());
            const availableAt = store === ANY_STORE ? toQty(stockItem?.available_qty) : toQty(stockItem?.available_by_location?.[store] ?? stockItem?.location_quantities?.[store]);
            const qty = Number(line.qty) || 0;
            const tone = !line.part_number ? 'muted' : !stockItem ? 'amber' : qty > availableAt ? 'red' : 'green';
            return (
              <div key={line.key} className="grid grid-cols-[minmax(0,1fr)_84px_minmax(0,0.9fr)_32px] items-center gap-2">
                <div className="min-w-0">
                  <Input
                    list="delivery-plan-parts"
                    value={line.part_number}
                    onChange={(event) => setLine(line.key, 'part_number', event.target.value)}
                    placeholder="Part no."
                    className="h-9 font-mono text-[12.5px]"
                    aria-label={`Part number, line ${index + 1}`}
                    data-testid="delivery-plan-part"
                  />
                  {stockItem?.description ? <p className="mt-0.5 truncate text-[11px] text-ink-3">{stockItem.description}</p> : null}
                </div>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={line.qty}
                  onChange={(event) => setLine(line.key, 'qty', event.target.value)}
                  placeholder="Qty"
                  className="h-9 text-right"
                  aria-label={`Quantity, line ${index + 1}`}
                  data-testid="delivery-plan-qty"
                />
                <span className={cn('truncate text-[12px] font-semibold', {
                  'text-ink-4': tone === 'muted',
                  'text-status-amber-ink': tone === 'amber',
                  'text-status-red-ink': tone === 'red',
                  'text-status-green-ink': tone === 'green'
                })}>
                  {tone === 'muted' ? '—' : tone === 'amber' ? 'Not in stock list' : `${formatNumber(availableAt)} available${store === ANY_STORE ? '' : ` in ${locationShort(store)}`}`}
                </span>
                <Button type="button" size="icon-sm" variant="ghost" className="text-ink-3" onClick={() => removeLine(line.key)} aria-label={`Remove line ${index + 1}`}>
                  <X aria-hidden="true" />
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-[11.5px] text-ink-3">Availability is a guide for planning — stock is only taken when the Site DN is recorded.</p>
      </SidePanelSection>

      <label className="block">
        <span className={fieldLabel}>Notes</span>
        <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="e.g. Site wants the sensors before the ceiling closes" className="min-h-[64px]" maxLength={1000} />
      </label>
    </SidePanel>
  );
}
